import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './PickListList.css';

function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function failureCountsFromPickList(pl) {
  if (!pl) return { unavailable: 0, short: 0 };
  if (pl.unavailableLineCount != null || pl.shortLineCount != null) {
    return {
      unavailable: Number(pl.unavailableLineCount) || 0,
      short: Number(pl.shortLineCount) || 0,
    };
  }
  const lines = pl.lines || [];
  let unavailable = 0;
  let short = 0;
  for (const l of lines) {
    const r = String(l.pickLineResult || '').toLowerCase();
    if (r === 'unavailable') unavailable += 1;
    else if (r === 'short') short += 1;
  }
  return { unavailable, short };
}

function displayPickListStatus(pl) {
  const st = String(pl.status || 'pending').toLowerCase();
  const { unavailable, short } = failureCountsFromPickList(pl);
  if (unavailable > 0) return 'failed';
  if (st === 'dispatched' && short > 0) return 'failed';
  return st;
}

function formatStatusLabel(s) {
  const x = String(s || 'pending').toLowerCase();
  if (x === 'failed') return 'Failed';
  return x.charAt(0).toUpperCase() + x.slice(1);
}

function formatPickLineResultLabel(r) {
  const x = String(r || '').toLowerCase();
  if (x === 'unavailable') return 'N/A';
  if (x === 'short') return 'Short';
  if (x === 'ok') return 'OK';
  return r || '—';
}

function formatPickListDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString();
}

const PickListList = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, isAdmin } = useAuth();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailTab, setDetailTab] = useState(0);
  const [printModal, setPrintModal] = useState({ open: false, pickList: null, pdfUrl: null, pdfError: null });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTab, setAssignTab] = useState(0);
  const [pickers, setPickers] = useState([]);
  const [pendingAssign, setPendingAssign] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSavingId, setAssignSavingId] = useState(null);
  const [assignError, setAssignError] = useState('');
  const [autoAssignResult, setAutoAssignResult] = useState(null);
  const [reopenSaving, setReopenSaving] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setListLoading(true);
      setError('');
      const res = await fetchWithAuth('/api/pick-lists');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load pick lists');
      }
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setListLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadDetail = useCallback(
    async (id) => {
      if (id == null) {
        setDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        setError('');
        const res = await fetchWithAuth(`/api/pick-lists/${id}`);
        if (!res.ok) throw new Error('Failed to load pick list');
        const data = await res.json();
        setDetail(data);
      } catch (err) {
        setError(err.message || 'Failed to load detail');
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [fetchWithAuth]
  );

  useEffect(() => {
    if (selectedId == null) {
      setDetail(null);
      return;
    }
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const filteredList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((pl) => {
      const parts = [
        String(pl.id),
        pl.obdNumber,
        pl.orderNumber,
        pl.customerNumber,
        pl.status,
        pl.assignedPickerDisplayName,
        pl.assignedPickerEmail,
        formatPickListDate(pl.createdAt),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return parts.includes(q);
    });
  }, [list, searchQuery]);

  const handleBack = () => navigate('/pick-list');

  const handleRowClick = (pl) => {
    setSelectedId(pl.id);
    setDetailTab(0);
  };

  const openPrintModal = useCallback(
    async (pl, e) => {
      if (e) e.stopPropagation();
      setPrintModal({ open: true, pickList: pl, pdfUrl: null, pdfError: null });
      try {
        const res = await fetchWithAuth(`/api/pick-lists/${pl.id}/pdf`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setPrintModal((prev) => ({ ...prev, pdfError: data.message || 'PDF not available' }));
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPrintModal((prev) => ({ ...prev, pdfUrl: url }));
      } catch (err) {
        setPrintModal((prev) => ({ ...prev, pdfError: err.message || 'Failed to load PDF' }));
      }
    },
    [fetchWithAuth]
  );

  const closePrintModal = useCallback(() => {
    setPrintModal((prev) => {
      if (prev.pdfUrl) URL.revokeObjectURL(prev.pdfUrl);
      return { open: false, pickList: null, pdfUrl: null, pdfError: null };
    });
  }, []);

  const printTarget = detail || (selectedId != null ? list.find((p) => sameId(p.id, selectedId)) : null);

  const loadAssignmentModalData = useCallback(async () => {
    setAssignError('');
    setAutoAssignResult(null);
    setAssignLoading(true);
    try {
      const [pr, pe] = await Promise.all([
        fetchWithAuth('/api/users/pickers'),
        fetchWithAuth('/api/pick-lists/assignment/pending'),
      ]);
      const prData = await pr.json().catch(() => ({}));
      const peData = await pe.json().catch(() => ({}));
      if (!pr.ok) throw new Error(prData.message || 'Failed to load pickers');
      if (!pe.ok) throw new Error(peData.message || 'Failed to load pending lists');
      setPickers(Array.isArray(prData) ? prData : []);
      setPendingAssign(Array.isArray(peData) ? peData : []);
    } catch (err) {
      setAssignError(err.message || 'Failed to load assignment data');
    } finally {
      setAssignLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (assignOpen && isAdmin) loadAssignmentModalData();
  }, [assignOpen, isAdmin, loadAssignmentModalData]);

  const patchPickListAssignment = async (pickListId, assignedPickerUserId) => {
    const res = await fetchWithAuth(`/api/pick-lists/${pickListId}/assignment`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignedPickerUserId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Update failed');
    return data;
  };

  const handleManualPickerChange = async (row, pickerIdStr) => {
    const assignedPickerUserId = pickerIdStr === '' ? null : Number(pickerIdStr);
    setAssignSavingId(row.id);
    setAssignError('');
    try {
      const updated = await patchPickListAssignment(row.id, assignedPickerUserId);
      setPendingAssign((prev) =>
        prev.map((r) =>
          r.id === updated.id
            ? {
                ...r,
                assignedPickerUserId: updated.assignedPickerUserId,
                assignedPickerDisplayName: updated.assignedPickerDisplayName,
                assignedPickerEmail: updated.assignedPickerEmail,
              }
            : r
        )
      );
      await loadList();
      if (selectedId != null && sameId(selectedId, updated.id)) {
        loadDetail(selectedId);
      }
    } catch (err) {
      setAssignError(err.message || 'Assignment failed');
    } finally {
      setAssignSavingId(null);
    }
  };

  const handleReopenForPicker = async () => {
    if (!detail?.id || !isAdmin) return;
    setReopenSaving(true);
    setError('');
    try {
      const res = await fetchWithAuth(`/api/pick-lists/${detail.id}/reopen`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Reopen failed');
      setDetail(data);
      await loadList();
    } catch (err) {
      setError(err.message || 'Reopen failed');
    } finally {
      setReopenSaving(false);
    }
  };

  const handleAutoAssign = async () => {
    setAssignError('');
    setAutoAssignResult(null);
    setAssignLoading(true);
    try {
      const res = await fetchWithAuth('/api/pick-lists/assignment/auto', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Auto-assign failed');
      setAutoAssignResult(data);
      await loadAssignmentModalData();
      await loadList();
      if (selectedId != null) loadDetail(selectedId);
    } catch (err) {
      setAssignError(err.message || 'Auto-assign failed');
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Layout>
      <Box className="pick-list-md-page" sx={{ p: 2 }}>
        <TypographyBlock />

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <div className="pick-list-md-toolbar">
          <Button
            type="button"
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Back
          </Button>
          <Divider orientation="vertical" flexItem />
          <Button
            type="button"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadList();
              if (selectedId != null) loadDetail(selectedId);
            }}
            disabled={listLoading}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          {isAdmin && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                startIcon={<PersonAddOutlinedIcon />}
                onClick={() => {
                  setAssignTab(0);
                  setAssignOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Assign
              </Button>
            </>
          )}
          {printTarget && (
            <>
              <Divider orientation="vertical" flexItem />
              <Button
                type="button"
                variant="contained"
                startIcon={<PrintOutlinedIcon />}
                onClick={() => openPrintModal(printTarget)}
                sx={{ textTransform: 'none' }}
              >
                Print PDF
              </Button>
            </>
          )}
        </div>

        <div className="pick-list-md-split">
          <div className="pick-list-md-master">
            <div className="pick-list-md-section-header">Pick lists</div>
            <div className="pick-list-md-search">
              <TextField
                size="small"
                fullWidth
                label="Search"
                placeholder="ID, OBD, order, customer…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                margin="dense"
                sx={{ mt: 0 }}
              />
            </div>
            <div className="pick-list-md-list-scroll">
              {listLoading ? (
                <p className="pick-list-md-muted">Loading…</p>
              ) : filteredList.length === 0 ? (
                <p className="pick-list-md-muted">
                  {list.length === 0
                    ? 'No pick lists yet. Import one from Pick List Import.'
                    : 'No matches.'}
                </p>
              ) : (
                <table className="pick-list-md-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Order</th>
                      <th>Picker</th>
                      <th>Status</th>
                      <th className="pick-list-md-th-narrow">Print</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((pl) => {
                      const disp = displayPickListStatus(pl);
                      return (
                      <tr
                        key={pl.id}
                        className={sameId(selectedId, pl.id) ? 'pick-list-md-row-selected' : ''}
                        onClick={() => handleRowClick(pl)}
                      >
                        <td>{pl.id}</td>
                        <td>{pl.orderNumber || '—'}</td>
                        <td className="pick-list-md-td-picker" title={pl.assignedPickerEmail || ''}>
                          {pl.assignedPickerDisplayName || pl.assignedPickerEmail || '—'}
                        </td>
                        <td>
                          <span className={`pick-list-status-badge pick-list-status-${disp}`}>
                            {formatStatusLabel(disp)}
                          </span>
                        </td>
                        <td className="pick-list-md-td-actions" onClick={(e) => openPrintModal(pl, e)}>
                          <button
                            type="button"
                            className="pick-list-md-print-btn"
                            title="Print / view PDF with QR code"
                            aria-label="Print"
                          >
                            <PrintOutlinedIcon sx={{ fontSize: 20 }} />
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="pick-list-md-detail">
            <div className="pick-list-md-section-header">
              {detail
                ? `Pick list #${detail.id}`
                : selectedId != null && detailLoading
                  ? 'Loading…'
                  : 'Pick list details'}
            </div>
            {!selectedId ? (
              <div className="pick-list-md-detail-empty">Select a pick list from the list.</div>
            ) : detailLoading && !detail ? (
              <div className="pick-list-md-detail-empty">Loading details…</div>
            ) : !detail ? (
              <div className="pick-list-md-detail-empty">Could not load this pick list.</div>
            ) : (
              <>
                <Tabs
                  value={detailTab}
                  onChange={(_, v) => setDetailTab(v)}
                  sx={{ borderBottom: 1, borderColor: 'divider', px: 1, minHeight: 42 }}
                >
                  <Tab label="Overview" sx={{ textTransform: 'none', minHeight: 42 }} />
                  <Tab label={`Lines (${detail.lines?.length ?? 0})`} sx={{ textTransform: 'none', minHeight: 42 }} />
                  <Tab label="Print" sx={{ textTransform: 'none', minHeight: 42 }} />
                </Tabs>
                <div className="pick-list-md-detail-body">
                  {detailTab === 0 && (
                    <div className="pick-list-overview-grid">
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">OBD number</span>
                        <span className="pick-list-overview-value">{detail.obdNumber || '—'}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">OBD date</span>
                        <span className="pick-list-overview-value">{formatPickListDate(detail.obdDate)}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Order</span>
                        <span className="pick-list-overview-value">{detail.orderNumber || '—'}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Customer</span>
                        <span className="pick-list-overview-value">{detail.customerNumber || '—'}</span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Status</span>
                        <span className="pick-list-overview-value">
                          <span
                            className={`pick-list-status-badge pick-list-status-${displayPickListStatus(detail)}`}
                          >
                            {formatStatusLabel(displayPickListStatus(detail))}
                          </span>
                        </span>
                      </div>
                      <div className="pick-list-overview-item">
                        <span className="pick-list-overview-label">Created</span>
                        <span className="pick-list-overview-value">{formatPickListDate(detail.createdAt)}</span>
                      </div>
                      <div className="pick-list-overview-item pick-list-overview-item--wide">
                        <span className="pick-list-overview-label">Assigned picker</span>
                        <span className="pick-list-overview-value">
                          {detail.assignedPickerDisplayName || detail.assignedPickerEmail || '—'}
                        </span>
                      </div>
                      {isAdmin &&
                        String(detail.status || '').toLowerCase() !== 'dispatched' &&
                        (detail.lines || []).some((l) => String(l.pickLineResult || '').toLowerCase() === 'unavailable') && (
                          <div className="pick-list-overview-item pick-list-overview-item--wide">
                            <span className="pick-list-overview-label">Not available lines</span>
                            <span className="pick-list-overview-value" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                              <span>Clear N/A marks so the assigned picker can continue on mobile.</span>
                              <Button
                                type="button"
                                variant="contained"
                                color="warning"
                                size="small"
                                disabled={reopenSaving}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReopenForPicker();
                                }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                {reopenSaving ? 'Working…' : 'Reopen for picker'}
                              </Button>
                            </span>
                          </div>
                        )}
                    </div>
                  )}
                  {detailTab === 1 && (
                    <div className="pick-list-lines-wrap">
                      <table className="pick-list-lines-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Part number</th>
                            <th>Description</th>
                            <th>Qty</th>
                            <th>Bin</th>
                            <th>Picked</th>
                            <th>Result</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.lines || []).map((line, i) => (
                            <tr key={line.id || i}>
                              <td>{i + 1}</td>
                              <td>{line.partNumber || '—'}</td>
                              <td>{line.partDescription || '—'}</td>
                              <td>
                                {line.quantity} {line.quantityUnit || 'EA'}
                              </td>
                              <td>{line.binNumber || '—'}</td>
                              <td>{line.pickedQty != null ? line.pickedQty : '—'}</td>
                              <td>{formatPickLineResultLabel(line.pickLineResult)}</td>
                              <td>{line.failureReason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {detailTab === 2 && (
                    <div className="pick-list-print-tab">
                      <p className="pick-list-print-tab-desc">
                        Open the PDF with embedded QR code so operators can scan from a printed sheet or on-screen.
                      </p>
                      <Button
                        type="button"
                        variant="contained"
                        size="large"
                        startIcon={<PrintOutlinedIcon />}
                        onClick={() => openPrintModal(detail)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Open print preview (PDF)
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Assign pick lists</DialogTitle>
          <DialogContent dividers>
            {assignError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError('')}>
                {assignError}
              </Alert>
            )}
            <Tabs
              value={assignTab}
              onChange={(_, v) => {
                setAssignTab(v);
                setAutoAssignResult(null);
              }}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label="Manual" sx={{ textTransform: 'none' }} />
              <Tab label="Auto" sx={{ textTransform: 'none' }} />
            </Tabs>
            {assignLoading && pendingAssign.length === 0 && pickers.length === 0 ? (
              <p className="pick-list-md-muted">Loading…</p>
            ) : null}
            {assignTab === 0 && (
              <>
                <p style={{ margin: '0 0 12px', fontSize: 14, color: '#4b5563' }}>
                  Pending pick lists only. Choose a user with the <strong>Picker</strong> role (configured under
                  Users). Pickers only see lists assigned to them in the mobile app.
                </p>
                {pickers.length === 0 && !assignLoading ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No picker users found. Add users with role &quot;Picker&quot; in Users, then return here.
                  </Alert>
                ) : null}
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Order</TableCell>
                        <TableCell>OBD</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>Picker</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingAssign.length === 0 && !assignLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                            No pending pick lists to assign.
                          </TableCell>
                        </TableRow>
                      ) : (
                        pendingAssign.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.orderNumber || '—'}</TableCell>
                            <TableCell>{row.obdNumber || '—'}</TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth disabled={assignSavingId === row.id}>
                                <InputLabel id={`picker-${row.id}`}>Picker</InputLabel>
                                <Select
                                  labelId={`picker-${row.id}`}
                                  label="Picker"
                                  value={
                                    row.assignedPickerUserId != null ? String(row.assignedPickerUserId) : ''
                                  }
                                  onChange={(e) => handleManualPickerChange(row, e.target.value)}
                                >
                                  <MenuItem value="">
                                    <em>Unassigned</em>
                                  </MenuItem>
                                  {pickers.map((p) => (
                                    <MenuItem key={p.id} value={String(p.id)}>
                                      {p.displayName || p.email} ({p.email})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {assignTab === 1 && (
              <Box>
                <p style={{ margin: '0 0 12px', fontSize: 14, color: '#4b5563', lineHeight: 1.55 }}>
                  Distributes <strong>unassigned</strong> pending pick lists across all picker users using a
                  load-balanced algorithm (random tie-break). Existing assignments are kept; only rows with no picker
                  are filled.
                </p>
                {pickers.length === 0 && !assignLoading ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No picker users available for auto-assign.
                  </Alert>
                ) : null}
                <Button
                  variant="contained"
                  onClick={handleAutoAssign}
                  disabled={assignLoading || pickers.length === 0}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {assignLoading ? 'Running…' : 'Run auto-assign'}
                </Button>
                {autoAssignResult && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Assigned <strong>{autoAssignResult.assigned}</strong> pick list(s) across{' '}
                    <strong>{autoAssignResult.pickerCount}</strong> picker(s).
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignOpen(false)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {printModal.open && (
          <div className="pick-list-print-overlay" onClick={closePrintModal} role="dialog" aria-modal="true" aria-label="Pick list PDF with QR code">
            <div className="pick-list-print-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pick-list-print-header">
                <span>
                  Pick list #{printModal.pickList?.id} – PDF includes QR code. Print to get pick list with QR for mobile
                  scan.
                </span>
                <button type="button" className="print-modal-close" onClick={closePrintModal} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="pick-list-print-body">
                {printModal.pdfError && <div className="pick-list-pdf-error">{printModal.pdfError}</div>}
                {printModal.pdfUrl && (
                  <iframe title="Pick list PDF (with QR code for mobile scan)" src={printModal.pdfUrl} className="pick-list-pdf-iframe" />
                )}
              </div>
            </div>
          </div>
        )}
      </Box>
    </Layout>
  );
};

function TypographyBlock() {
  return (
    <div className="pick-list-md-intro">
      <h1 className="home-menu-title">Pick List</h1>
    </div>
  );
}

export default PickListList;
